import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import SfmcAccounts from './pages/SfmcAccounts';
import MonitoringConfigs from './pages/MonitoringConfigs';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import InvoiceHistory from './pages/InvoiceHistory';
import AccountBilling from './pages/AccountBilling';
import SfmcExplorer from './pages/SfmcExplorer';
import MonitoringTrends from './pages/MonitoringTrends';
import AlertHistory from './pages/AlertHistory';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AuditLog from './pages/AuditLog';
import SystemHealth from './pages/SystemHealth';
import BackupManagement from './pages/BackupManagement';
import Reports from "./pages/Reports";
import Audit from "./pages/Audit";
import AdminCrm from "./pages/AdminCrm";
import ReportDetail from "./pages/ReportDetail";

// Components
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Version checker and display
const useVersionDisplay = () => {
  const [version, setVersion] = React.useState('...');

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch('/api/version');
        if (response.ok) {
          const data = await response.json();
          setVersion(data.version || '...');
        }
      } catch (err) {
        console.log('Version fetch failed:', err);
      }
    };

    fetchVersion();
  }, []);

  return version;
};

function PrivateRoute({ children }) {
  const { token, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { token, user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!token) {
    return <Navigate to="/login" />;
  }

  // Security: Check admin role before rendering admin routes
  if (user?.role !== 'admin' && user?.role !== 'owner') {
    return <Navigate to="/dashboard" />;
  }

  return <AdminLayout>{children}</AdminLayout>;
}

function AppRoutes() {
  const { token } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/register" element={token ? <Navigate to="/dashboard" /> : <Register />} />
      <Route path="/forgot-password" element={token ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={token ? <Navigate to="/dashboard" /> : <ResetPassword />} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/sfmc-accounts" element={<PrivateRoute><SfmcAccounts /></PrivateRoute>} />
      <Route path="/monitoring" element={<PrivateRoute><MonitoringConfigs /></PrivateRoute>} />
      <Route path="/explorer" element={<PrivateRoute><SfmcExplorer /></PrivateRoute>} />
      <Route path="/trends" element={<PrivateRoute><MonitoringTrends /></PrivateRoute>} />
      <Route path="/alerts" element={<PrivateRoute><AlertHistory /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/invoices" element={<PrivateRoute><InvoiceHistory /></PrivateRoute>} />
      <Route path="/billing" element={<PrivateRoute><AccountBilling /></PrivateRoute>} />
      <Route path="/audit" element={<PrivateRoute><Audit /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="/reports/:id" element={<PrivateRoute><ReportDetail /></PrivateRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute><AuditLog /></AdminRoute>} />
      <Route path="/admin/health" element={<AdminRoute><SystemHealth /></AdminRoute>} />
      <Route path="/admin/backups" element={<AdminRoute><BackupManagement /></AdminRoute>} />
      <Route path="/admin/crm" element={<AdminRoute><AdminCrm /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  const version = useVersionDisplay();

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <div style={{
            position: 'fixed',
            bottom: '10px',
            left: '10px',
            fontSize: '11px',
            color: '#9ca3af',
            zIndex: 9999,
            fontFamily: 'monospace'
          }}>
            v{version}
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
